import Head from 'next/head';
import {useRouter} from 'next/router';

import {BrowseCourseBadgesContext} from '@udemy/browse-course';
import {HeadSEO} from '@udemy/head-seo';
import {useFeatures} from '@udemy/react-experimentation-provider';
import {SubscriptionPlanProductType, SubscriptionPlansProvider} from '@udemy/subscription-graphql';

import {getLayout} from 'src/components/layout';
import {SubscriptionCTAExperimentProvider} from 'src/contexts/subscription-cta-experiment-context';
import {useCanonicalUrl} from 'src/hooks/useCanonicalUrl';
import {META_DEFAULT_BASE_URL} from 'src/lib/constants';
import {
    PERSONAL_PLAN_REVAMP,
    SUBSCRIPTION_CATALOG_DIFFERENTIATOR_FEATURE,
} from 'src/lib/experimentation-constants';
import {getBrowseCourseBadgesContextOptionsByExperiment} from 'src/lib/experimentation-helper';

import {PersonalPlanLandingPageBody} from './personal-plan-landing-page-body.react-component';

export function PersonalPlanLandingPageFeature() {
    const router = useRouter();
    const {url: canonicalUrl} = useCanonicalUrl([], META_DEFAULT_BASE_URL);

    // TODO: Clean up after subs catalog diff experiment is over https://udemyjira.atlassian.net/browse/CVR-151
    const {featureVariantAssignments} = useFeatures([
        SUBSCRIPTION_CATALOG_DIFFERENTIATOR_FEATURE,
        PERSONAL_PLAN_REVAMP,
    ]);

    // https://udemy.atlassian.net/browse/CVR-1472
    const subscriptionCatalogDifferentiatorFeature =
        featureVariantAssignments.get(SUBSCRIPTION_CATALOG_DIFFERENTIATOR_FEATURE)?.configuration ??
        {};

    const isPersonalPlanRevampEnabled =
        featureVariantAssignments.get(PERSONAL_PLAN_REVAMP)?.configuration ?? {};

    return (
        <>
            <HeadSEO
                canonicalUrl={canonicalUrl}
                assetPath={process.env.ASSET_PREFIX}
                headWrapper={Head}
            />
            <SubscriptionCTAExperimentProvider
                context={{
                    showCancelAnytime: true,
                }}
            >
                <SubscriptionPlansProvider
                    subscriptionPlanQueryVariables={[
                        {
                            filters: {
                                productType: SubscriptionPlanProductType.Consumersubscription,
                            },
                            marketingURL: `${new URL(canonicalUrl).origin}${router.asPath}`,
                        },
                        {
                            filters: {
                                productType:
                                    SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle,
                            },
                            marketingURL: `${new URL(canonicalUrl).origin}${router.asPath}`,
                        },
                        {
                            filters: {
                                productType: SubscriptionPlanProductType.GrowWithGoogle,
                            },
                            marketingURL: `${new URL(canonicalUrl).origin}${router.asPath}`,
                        },
                    ]}
                >
                    <BrowseCourseBadgesContext.Provider
                        value={{
                            browseBadgeHandlerOptions:
                                getBrowseCourseBadgesContextOptionsByExperiment(
                                    subscriptionCatalogDifferentiatorFeature,
                                ),
                        }}
                    >
                        <PersonalPlanLandingPageBody
                            isPPLPRevamp={isPersonalPlanRevampEnabled?.isPPLPRevamp}
                        />
                    </BrowseCourseBadgesContext.Provider>
                </SubscriptionPlansProvider>
            </SubscriptionCTAExperimentProvider>
        </>
    );
}

PersonalPlanLandingPageFeature.getLayout = getLayout;
